import {
  BURN_DEGREE_OPTIONS,
  DRAINAGE_AMOUNTS,
  DRAINAGE_TYPES,
  EDGE_OPTIONS,
  ODOR_TYPES,
  PRESSURE_STAGE_OPTIONS,
  SURROUNDING_TISSUE_OPTIONS,
  THICKNESS_OPTIONS,
  WOUND_BED_TISSUE_TYPES,
} from '../constants/wounds';
import type { Wound } from '../types/wound.types';

/**
 * Read-only display of a RogerDocs wound draft (QA and coding review).
 *
 * A draft has one of two shapes. A Kinnser draft uses size_length,
 * stage_1..stage_susp_dti, treatment and additional_information, and
 * one boolean per thickness, edge and surrounding-tissue choice,
 * color_*_percentage wound bed fields, tunneling_length and
 * wound_pain_intensity. A RogerEMR draft uses length_cm, wound_stage /
 * burn_degree, treatment_performed and additional_info, plus enum and list
 * assessment fields. Every reader here falls back from one shape to the
 * other, and a missing value reads as null so the modal can drop empty
 * sections.
 */

type Option = { value: string; label: string };

/** A value worth showing, as text; null for blank, missing, or non-finite. */
export function displayText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  return null;
}

function optionLabel(options: Option[], value: unknown): string | null {
  const text = displayText(value);
  if (text === null) return null;
  return options.find(o => o.value === text)?.label ?? text;
}

function optionLabels(options: Option[], values: unknown): string | null {
  if (!Array.isArray(values)) return null;
  const labels = values
    .map(v => optionLabel(options, v))
    .filter((l): l is string => l !== null);
  return labels.length > 0 ? labels.join(', ') : null;
}

function withUnit(value: unknown, unit: string): string | null {
  const text = displayText(value);
  return text === null ? null : `${text}${unit}`;
}

function withOther(main: string | null, other: unknown): string | null {
  const otherText = displayText(other);
  if (main === null) return otherText;
  return otherText === null ? main : `${main} (${otherText})`;
}

const isBareNumber = (text: string): boolean => /^\d+$/.test(text);

/**
 * A clock position. RogerEMR stores a bare number (3); Kinnser stores the
 * full text ("3 O'clock"), so the unit is added only to a bare number.
 */
function clockText(value: unknown): string | null {
  const text = displayText(value);
  if (text === null) return null;
  return isBareNumber(text) ? `${text} o'clock` : text;
}

function yesNo(value: unknown): string | null {
  if (value === 'yes' || value === true) return 'Yes';
  if (value === 'no' || value === false) return 'No';
  return null;
}

const PRESSURE_STAGE_LABELS: Record<string, string> = Object.fromEntries(
  PRESSURE_STAGE_OPTIONS.map(o => [o.value, o.label])
);

const BURN_DEGREE_LABELS: Record<string, string> = Object.fromEntries(
  BURN_DEGREE_OPTIONS.map(o => [o.value, o.label])
);

export function getWoundStage(w: Wound): string | null {
  if (w.stage_1) return 'Stage 1';
  if (w.stage_2) return 'Stage 2';
  if (w.stage_3) return 'Stage 3';
  if (w.stage_4) return 'Stage 4';
  if (w.stage_unstageable) return 'Unstageable';
  if (w.stage_susp_dti) return 'Suspected DTI';
  if (w.wound_type === 'burn') {
    const degree = displayText(w.burn_degree) ?? displayText(w.wound_stage);
    if (degree !== null) return BURN_DEGREE_LABELS[degree] ?? degree;
  }
  const stage = displayText(w.wound_stage);
  if (stage === null) return null;
  // RogerDocs passes an unrecognised stage through as typed.
  return PRESSURE_STAGE_LABELS[stage] ?? stage;
}

/**
 * Size is one section with one flag. The Roger app keeps the last measured
 * values when the clinician marks size not assessed, so they are hidden
 * while size_na is set; otherwise Size would show under Not Assessed next
 * to a length.
 */
export function getWoundMeasurements(w: Wound): {
  length: string | null;
  width: string | null;
  depth: string | null;
} {
  if (w.size_na === true) return { length: null, width: null, depth: null };
  return {
    length: displayText(w.size_length) ?? displayText(w.length_cm),
    width: displayText(w.size_width) ?? displayText(w.width_cm),
    depth: displayText(w.size_depth) ?? displayText(w.depth_cm),
  };
}

export const getWoundTreatment = (w: Wound): string | null =>
  displayText(w.treatment) ?? displayText(w.treatment_performed);

export const getWoundAdditionalInfo = (w: Wound): string | null =>
  displayText(w.additional_information) ?? displayText(w.additional_info);

/**
 * A RogerEMR section toggle (drainage, odor, undermining, tunneling) is
 * yes / no / not_assessed. The Roger app keeps a section's sub-fields when
 * the toggle moves off "yes", and the RogerDocs push to the chart
 * (_transform_wound_draft_to_assessment) drops them; read them the same way.
 * A Kinnser draft has no toggle, so its sub-fields count unless the
 * section's *_not_assessed flag is set.
 */
function toggleAllowsDetail(toggle: unknown): boolean {
  return typeof toggle !== 'string' || toggle === '' || toggle === 'yes';
}

const drainageAllowsDetail = (w: Wound): boolean =>
  toggleAllowsDetail(w.drainage) && w.drainage_not_assessed !== true;

const odorAllowsDetail = (w: Wound): boolean =>
  toggleAllowsDetail(w.odor) && w.odor_not_assessed !== true;

/** Kinnser writes "None" where no value was chosen. */
function withoutNoneSentinel(value: string | null): string | null {
  return value === null || value.toLowerCase() === 'none' ? null : value;
}

export const getDrainageType = (w: Wound): string | null =>
  drainageAllowsDetail(w)
    ? withoutNoneSentinel(optionLabel(DRAINAGE_TYPES, w.drainage_type))
    : null;

export const getDrainageAmount = (w: Wound): string | null =>
  drainageAllowsDetail(w)
    ? withoutNoneSentinel(optionLabel(DRAINAGE_AMOUNTS, w.drainage_amount))
    : null;

export const getDrainageNotes = (w: Wound): string | null =>
  drainageAllowsDetail(w)
    ? withoutNoneSentinel(
        displayText(w.drainage_other) ?? displayText(w.drainage_type_other_text)
      )
    : null;

export const getOdorType = (w: Wound): string | null =>
  odorAllowsDetail(w)
    ? withoutNoneSentinel(optionLabel(ODOR_TYPES, w.odor_type))
    : null;

export const getOdorNotes = (w: Wound): string | null =>
  odorAllowsDetail(w)
    ? withoutNoneSentinel(
        displayText(w.odor_other) ?? displayText(w.odor_type_other_text)
      )
    : null;

function formatUnderminingEntries(w: Wound): string | null {
  if (!toggleAllowsDetail(w.undermining)) return null;
  if (w.undermining_not_assessed === true) return null;
  if (!Array.isArray(w.undermining_entries)) return null;
  const parts = w.undermining_entries
    .map(entry => {
      const from = displayText(entry?.from_direction);
      const to = displayText(entry?.to_direction);
      const depth = displayText(entry?.depth_cm);
      let clock: string | null = null;
      if (from !== null && to !== null) {
        clock =
          isBareNumber(from) && isBareNumber(to)
            ? `${from} to ${to} o'clock`
            : `${clockText(from)} to ${clockText(to)}`;
      }
      const cm = depth === null ? null : `${depth} cm`;
      const text = [clock, cm].filter(Boolean).join(', ');
      return text === '' ? null : text;
    })
    .filter((t): t is string => t !== null);
  return parts.length > 0 ? parts.join('; ') : null;
}

/**
 * The first undermining row of a Kinnser draft is flat:
 * undermining_from_direction, undermining_direction (to) and
 * undermining_length. The modal shows length and direction in its own
 * undermining block; this is the from side of that row.
 */
export const getUnderminingFromDirection = (w: Wound): string | null =>
  w.undermining_not_assessed === true
    ? null
    : displayText(w.undermining_from_direction);

type KinnserChoice = { key: string; label: string };

function constantLabel(options: Option[], value: string): string {
  const option = options.find(o => o.value === value);
  if (option === undefined) throw new Error(`Unknown wound option ${value}`);
  return option.label;
}

// One boolean per Kinnser checkbox. A label reuses the RogerEMR option label
// where that reads the same as the Kinnser screen.
const KINNSER_THICKNESS: KinnserChoice[] = [
  { key: 'thickness_partial', label: 'Partial Thickness' },
  { key: 'thickness_full', label: 'Full Thickness' },
  {
    key: 'thickness_undetermined',
    label: constantLabel(THICKNESS_OPTIONS, 'unable_to_determine'),
  },
  {
    key: 'thickness_epithelialized',
    label: constantLabel(THICKNESS_OPTIONS, 'epithelized'),
  },
  { key: 'thickness_other', label: constantLabel(THICKNESS_OPTIONS, 'other') },
];

const KINNSER_EDGES: KinnserChoice[] = [
  { key: 'edges_attached', label: constantLabel(EDGE_OPTIONS, 'attached') },
  {
    key: 'edges_non_attached',
    label: constantLabel(EDGE_OPTIONS, 'non_attached'),
  },
  {
    key: 'edges_epithelialization',
    label: constantLabel(EDGE_OPTIONS, 'epithelization'),
  },
  { key: 'edges_rolled', label: 'Rolled' },
  { key: 'edges_other', label: constantLabel(EDGE_OPTIONS, 'other') },
];

const tissueChoice = (value: string): KinnserChoice => ({
  key: `surrounding_tissue_${value}`,
  label: constantLabel(SURROUNDING_TISSUE_OPTIONS, value),
});

const KINNSER_SURROUNDING_TISSUE: KinnserChoice[] = [
  tissueChoice('intact'),
  tissueChoice('calloused'),
  tissueChoice('erythema'),
  tissueChoice('excoriated'),
  { key: 'surrounding_tissue_fragile', label: 'Fragile' },
  tissueChoice('indurated'),
  tissueChoice('macerated'),
  { key: 'surrounding_tissue_flesh_tone', label: 'Flesh tone' },
  tissueChoice('dry'),
  tissueChoice('other'),
];

// RogerEMR wound bed key -> the Kinnser color_*_percentage key.
const KINNSER_WOUND_BED_KEYS: Record<string, string> = {
  wound_bed_pink_red_pct: 'color_pink_red_percentage',
  wound_bed_granulation_pct: 'color_granulation_percentage',
  wound_bed_slough_pct: 'color_slough_percentage',
  wound_bed_eschar_pct: 'color_eschar_percentage',
  wound_bed_necrotic_pct: 'color_necrotic_percentage',
};

/** An extended Kinnser draft carries the section's checkbox booleans. */
const hasKinnserChoices = (w: Wound, choices: KinnserChoice[]): boolean =>
  choices.some(c => typeof w[c.key] === 'boolean');

/**
 * The checked Kinnser choices, with the section's Other text; null when the
 * section is flagged not assessed or nothing is checked or written. The Other
 * text is gated with the choices: the RogerDocs push drops it for a section
 * marked not assessed, and a retained description is not an assessment.
 */
function kinnserChoices(
  w: Wound,
  choices: KinnserChoice[],
  notAssessed: unknown,
  otherText: unknown
): string | null {
  if (notAssessed === true) return null;
  const labels = choices.filter(c => w[c.key] === true).map(c => c.label);
  return withOther(labels.length > 0 ? labels.join(', ') : null, otherText);
}

function kinnserWoundBedOther(w: Wound): string | null {
  const title = displayText(w.color_other_title);
  const pct = withUnit(w.color_other_values_percentage, '%');
  if (title !== null && pct !== null) return `${title}: ${pct}`;
  return title ?? pct;
}

/** RogerEMR gates tunneling with its toggle; Kinnser with a flag. */
const tunnelingAllowsDetail = (w: Wound): boolean =>
  toggleAllowsDetail(w.tunneling) && w.tunneling_not_assessed !== true;

export interface WoundDetailField {
  label: string;
  value: (w: Wound) => string | null;
}

export interface WoundDetailSection {
  title: string;
  fields: WoundDetailField[];
}

/**
 * The assessment sections, in chart order. Each field reads the RogerEMR
 * shape, or the extended Kinnser shape (checkbox booleans,
 * color_*_percentage, tunneling_length, wound_pain_intensity) when the draft
 * carries it. A section with no displayable field is dropped, so an older
 * Kinnser draft without the extended fields shows none.
 */
export const WOUND_DETAIL_SECTIONS: WoundDetailSection[] = [
  {
    title: 'Thickness',
    fields: [
      {
        label: 'Thickness',
        value: w =>
          hasKinnserChoices(w, KINNSER_THICKNESS)
            ? kinnserChoices(
                w,
                KINNSER_THICKNESS,
                w.thickness_not_assessed,
                w.thickness_other_text
              )
            : w.thickness_not_assessed === true
              ? null
              : withOther(
                  optionLabel(THICKNESS_OPTIONS, w.thickness),
                  w.thickness_other_text
                ),
      },
    ],
  },
  {
    title: 'Edges',
    fields: [
      {
        label: 'Edges',
        value: w =>
          hasKinnserChoices(w, KINNSER_EDGES)
            ? kinnserChoices(
                w,
                KINNSER_EDGES,
                w.edges_not_assessed,
                w.edges_other_text
              )
            : w.edges_not_assessed === true
              ? null
              : optionLabels(EDGE_OPTIONS, w.edges),
      },
    ],
  },
  {
    title: 'Wound Bed',
    fields: [
      ...WOUND_BED_TISSUE_TYPES.map(tissue => ({
        label: tissue.label,
        value: (w: Wound) =>
          w.wound_bed_not_assessed === true
            ? null
            : withUnit(
                w[tissue.key] ?? w[KINNSER_WOUND_BED_KEYS[tissue.key]],
                '%'
              ),
      })),
      {
        label: 'Other',
        value: w =>
          w.wound_bed_not_assessed === true
            ? null
            : (displayText(w.wound_bed_other_text) ?? kinnserWoundBedOther(w)),
      },
      {
        label: 'Color',
        value: w =>
          w.wound_bed_not_assessed === true
            ? null
            : withoutNoneSentinel(
                withOther(
                  displayText(w.wound_bed_color),
                  w.wound_bed_color_other
                )
              ),
      },
    ],
  },
  {
    title: 'Surrounding Tissue',
    fields: [
      {
        label: 'Tissue',
        value: w =>
          hasKinnserChoices(w, KINNSER_SURROUNDING_TISSUE)
            ? kinnserChoices(
                w,
                KINNSER_SURROUNDING_TISSUE,
                w.surrounding_tissue_not_assessed,
                w.surrounding_tissue_other_text
              )
            : w.surrounding_tissue_not_assessed === true
              ? null
              : withOther(
                  optionLabels(
                    SURROUNDING_TISSUE_OPTIONS,
                    w.surrounding_tissue
                  ),
                  w.surrounding_tissue_other_text
                ),
      },
    ],
  },
  {
    title: 'Undermining',
    fields: [
      { label: 'Present', value: w => yesNo(w.undermining) },
      { label: 'Entries', value: formatUnderminingEntries },
    ],
  },
  {
    title: 'Tunneling',
    fields: [
      { label: 'Present', value: w => yesNo(w.tunneling) },
      {
        label: 'Direction',
        value: w =>
          tunnelingAllowsDetail(w) ? clockText(w.tunneling_direction) : null,
      },
      {
        label: 'Depth',
        value: w =>
          tunnelingAllowsDetail(w)
            ? withUnit(w.tunneling_depth_cm, ' cm')
            : null,
      },
      {
        label: 'Length',
        value: w =>
          tunnelingAllowsDetail(w) ? withUnit(w.tunneling_length, ' cm') : null,
      },
    ],
  },
  {
    title: 'Pain',
    fields: [
      { label: 'Pain', value: w => yesNo(w.pain) },
      {
        label: 'Level',
        value: w =>
          w.pain === false
            ? null
            : withUnit(
                displayText(w.pain_level) ??
                  withoutNoneSentinel(displayText(w.wound_pain_intensity)),
                '/10'
              ),
      },
    ],
  },
  {
    title: 'Wound Care Notes',
    fields: [{ label: 'Notes', value: w => displayText(w.wound_care_notes) }],
  },
  {
    title: 'Discontinuation',
    fields: [
      { label: 'Date', value: w => displayText(w.date_discontinued) },
      {
        label: 'Reason',
        value: w =>
          withOther(
            displayText(w.discontinuation_reason),
            w.discontinuation_reason_other
          ),
      },
    ],
  },
];

export function getWoundDetailSections(
  w: Wound
): { title: string; rows: { label: string; value: string }[] }[] {
  return WOUND_DETAIL_SECTIONS.map(section => ({
    title: section.title,
    rows: section.fields
      .map(field => ({ label: field.label, value: field.value(w) }))
      .filter(
        (row): row is { label: string; value: string } => row.value !== null
      ),
  })).filter(section => section.rows.length > 0);
}

const NOT_ASSESSED_SECTION_LABELS: Record<string, string> = {
  size: 'Size',
  thickness: 'Thickness',
  edges: 'Edges',
  drainage: 'Drainage',
  odor: 'Odor',
  wound_bed: 'Wound Bed',
  surrounding_tissue: 'Surrounding Tissue',
  undermining: 'Undermining',
  tunneling: 'Tunneling',
};

function humanizeKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Whether a section's own flag says it was assessed. Neither app clears a
 * reason when the section is later assessed, so a reason only counts while
 * the flag still says not assessed. A RogerEMR draft flags drainage, odor,
 * undermining and tunneling with its toggle; a Kinnser draft, and every
 * other section, with an explicit *_not_assessed boolean. A section with no
 * flag in the draft (a key the draft does not carry) keeps its reason.
 */
function sectionFlaggedAssessed(w: Wound, key: string): boolean {
  switch (key) {
    case 'drainage':
    case 'odor':
    case 'undermining':
    case 'tunneling': {
      const toggle = w[key];
      if (typeof toggle === 'string' && toggle !== '') {
        return toggle !== 'not_assessed';
      }
      return w[`${key}_not_assessed`] === false;
    }
    case 'thickness':
    case 'edges':
    case 'wound_bed':
    case 'surrounding_tissue':
      return w[`${key}_not_assessed`] === false;
    default:
      return false;
  }
}

/**
 * One row per section the clinician marked not assessed, with the reason
 * when one was given. Reads the draft's per-section flags and reasons and
 * the not_assessed_reasons map the Roger app writes; a section named by
 * both shows once, and a reason left over from before the section was
 * assessed is dropped.
 */
export function getNotAssessedRows(
  w: Wound
): { label: string; reason: string | null }[] {
  const reasons = new Map<string, string | null>();
  const mark = (key: string, flagged: boolean, reason: unknown) => {
    if (sectionFlaggedAssessed(w, key)) return;
    const text = displayText(reason);
    if (!flagged && text === null) return;
    if (reasons.get(key) == null) reasons.set(key, text);
  };

  // A RogerEMR draft flags drainage, odor, undermining and tunneling with
  // its toggle; a Kinnser draft with a *_not_assessed boolean.
  mark('size', w.size_na === true, null);
  mark(
    'thickness',
    w.thickness_not_assessed === true,
    w.thickness_not_assessed_reason
  );
  mark('edges', w.edges_not_assessed === true, w.edges_not_assessed_reason);
  mark(
    'drainage',
    w.drainage === 'not_assessed' || w.drainage_not_assessed === true,
    w.drainage_not_assessed_reason
  );
  mark(
    'odor',
    w.odor === 'not_assessed' || w.odor_not_assessed === true,
    w.odor_not_assessed_reason
  );
  mark(
    'wound_bed',
    w.wound_bed_not_assessed === true,
    w.wound_bed_not_assessed_reason
  );
  mark(
    'surrounding_tissue',
    w.surrounding_tissue_not_assessed === true,
    w.surrounding_tissue_not_assessed_reason
  );
  mark(
    'undermining',
    w.undermining === 'not_assessed' || w.undermining_not_assessed === true,
    w.undermining_not_assessed_reason
  );
  mark(
    'tunneling',
    w.tunneling === 'not_assessed' || w.tunneling_not_assessed === true,
    w.tunneling_not_assessed_reason
  );

  const scraped = w.not_assessed_reasons;
  if (scraped && typeof scraped === 'object' && !Array.isArray(scraped)) {
    for (const [key, reason] of Object.entries(scraped)) {
      mark(key, true, reason);
    }
  }

  return [...reasons.entries()].map(([key, reason]) => ({
    label: NOT_ASSESSED_SECTION_LABELS[key] ?? humanizeKey(key),
    reason,
  }));
}
